import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartAddEvent.eventName, () => {
  setTimeout(() => {
    this.showDialog();
    setTimeout(customUpdateDiscountProgress, 500);
  }, 300);
});
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
function customUpdateDiscountProgress() {
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      const itemCount = cart.item_count;

      let text = '';
      let progress = 0;

      if (itemCount <= 1) {
        text = "You're 1 product away from an EXTRA 15% discount!";
        progress = 20;
      } else if (itemCount === 2) {
        text = "You unlocked 15% discount!";
        progress = 40;
      } else if (itemCount === 3) {
        text = "You unlocked 20% discount!";
        progress = 60;
      } else if (itemCount === 4) {
        text = "You unlocked 25% discount!";
        progress = 80;
      } else {
        text = "You unlocked 30% discount!";
        progress = 100;
      }

      let bar = document.querySelector('.custom-cart-discount-progress');

      if (!bar) {
        const drawer =
          document.querySelector('cart-drawer dialog') ||
          document.querySelector('cart-drawer') ||
          document.querySelector('.cart-drawer');

        if (!drawer) return;

        bar = document.createElement('div');
        bar.className = 'custom-cart-discount-progress';
        bar.innerHTML = `
          <div class="custom-discount-text"></div>

          <div class="custom-discount-bar">
            <div class="custom-discount-fill"></div>
            <div class="custom-discount-step step-one">%</div>
            <div class="custom-discount-step step-two">%</div>
            <div class="custom-discount-step step-three">%</div>
            <div class="custom-discount-step step-four">%</div>
          </div>

          <div class="custom-discount-labels">
            <span>15% discount</span>
            <span>20% discount</span>
            <span>25% discount</span>
            <span>30% discount</span>
          </div>
        `;

        const header =
          drawer.querySelector('header') ||
          drawer.querySelector('.cart-drawer__header');

        if (header) {
          header.insertAdjacentElement('afterend', bar);
        } else {
          drawer.prepend(bar);
        }
      }

      bar.querySelector('.custom-discount-text').textContent = text;
      bar.querySelector('.custom-discount-fill').style.width = progress + '%';
      const steps = bar.querySelectorAll('.custom-discount-step');

steps.forEach(step => step.classList.remove('active'));

if (itemCount >= 2) steps[0].classList.add('active');
if (itemCount >= 3) steps[1].classList.add('active');
if (itemCount >= 4) steps[2].classList.add('active');
if (itemCount >= 5) steps[3].classList.add('active');
    });
}

document.addEventListener('DOMContentLoaded', customUpdateDiscountProgress);
document.addEventListener('cart:add', customUpdateDiscountProgress);
document.addEventListener('cart:update', customUpdateDiscountProgress);

const customDiscountStyle = document.createElement('style');
customDiscountStyle.innerHTML = `
.custom-cart-discount-progress {
  padding: 18px 24px 16px;
  text-align: center;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
}

.custom-discount-text {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 18px;
  color: #111;
}

.custom-discount-bar {
  position: relative;
  height: 6px;
  background: #e5e5e5;
  border-radius: 999px;
  margin: 0 28px;
}

.custom-discount-fill {
  height: 100%;
  background: #008060;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.custom-discount-step {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 26px;
  height: 26px;
  background: #d9d9d9;
  border: 2px solid #d9d9d9;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.custom-discount-step.active {
  background: #008060;
  border-color: #008060;
}

.step-one { left: 25%; }
.step-two { left: 50%; }
.step-three { left: 75%; }
.step-four { left: 100%; }

.custom-discount-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 11px;
  font-size: 10px;
  font-weight: 500;
  color: #555;
}
`;
document.head.appendChild(customDiscountStyle);